<script setup lang="ts">
    import KButton from "@/components/Button.vue";
    import { FORM_HELPERS } from "@/components/forms/Form/helpers";
    import { FormHelpers } from "@/components/forms/Form/types";
    import KAutocomplete from "@/components/forms/inputs/Autocomplete/KAutocomplete.vue";
    import { useStore } from "@/store";
    import { ReceptforskrivningMall } from "@/store/modules/recept/receptforskrivningMallModule";
    import { getDefaultFormValues, openNLLErrorDialog } from "@/tabs/Receptforskrivning/helpers";
    import { getMalltypLabel } from "@/tabs/Receptforskrivning/Mall/helpers";
    import emitter from "tiny-emitter/instance";
    import { computed, inject, onBeforeMount, onUnmounted, ref } from "vue";

    const store = useStore();

    const props = defineProps<{
        tabId: number;
    }>();

    const autocompleteRef = ref();
    /**
     * Används för att göra så att nästa körning av funktionen som anropas vid ändrat värde i autocomplete
     * inte gör något. Det är för att val av värde i autocomplete direkt uppdaterar värdet i formulärens data och
     * vid fall där en dialog visas och användaren kan klicka på nej/avbryt för att inte ändra värdet så måste vi
     * manuellt reverta värdet och då se till att onChange inte gör sin vanliga grej
     */
    const skipNextOnValue = ref(false);

    const currentProfileId = computed(() => store.getters["userData/currentProfileId"]);

    const formHelpers = inject<FormHelpers | null>(FORM_HELPERS, null);

    const onSearch = async ({ query }: { query?: string } = {}) => {
        try {
            const filterParams = {
                typ: "GEMENSAMPRIVAT",
            };
            const { subsetName } = await store.dispatch("receptforskrivningMall/fetchMallar", {
                filterParams: query ? { ...filterParams, q: query } : filterParams,
            });
            const result: ReceptforskrivningMall[] = store.getters["receptforskrivningMall/mallarSubset"]({
                subsetName,
                limit: 0,
                offset: 0,
            });

            return result.map((mall) => ({
                typ: mall.typ,
                label: `${mall.namn}, ${getMalltypLabel({
                    mall,
                    currentProfileId: currentProfileId?.value,
                }).toLowerCase()}`,
                value: mall.id,
            }));
        } catch (error: any) {
            console.error(error);

            openNLLErrorDialog({ error, description: "Kunde inte söka efter receptförskrivningsmallar." });

            return [];
        }
    };

    const setFormValues = async ({ mallId }: { mallId: string }) => {
        const mall: ReceptforskrivningMall = store.getters["receptforskrivningMall/mallById"]({ id: mallId });

        if (!mall?.forskrivning?.items?.[0]) {
            return;
        }

        const visa_administrering = [];
        const mallData = mall.forskrivning.items[0];

        if (mallData.administreringsmetod) {
            visa_administrering.push("metod");
        }

        if (mallData.administreringsvag) {
            visa_administrering.push("vag");
        }

        if (mallData.administreringsstalle) {
            visa_administrering.push("stalle");
        }

        if (mallData.medicinteknisk_produkt) {
            visa_administrering.push("medicinteknisk_produkt");
        }

        const patient = store.getters["patientData/patient"];

        store.commit("tabs/displaySpinner", { id: props.tabId, flag: true });

        await store.dispatch("lakemedel/fetchLakemedel", {
            filterParams: {
                npl_pack_ids: [mallData.lakemedelId],
            },
        });

        await store.dispatch("nll/fetchKodverkByLakemedel", {
            nplId: mallData.nplId,
            lakemedelId: mallData.lakemedelId,
        });

        store.commit("tabs/displaySpinner", { id: props.tabId, flag: false });

        formHelpers?.setFormValues({
            reset: true,
            triggerAsInitial: true,
            values: getDefaultFormValues({
                values: {
                    ...mall.forskrivning.items[0],
                    visa_administrering,
                    administrering_all: true,
                    formansberattigad_patient: !!patient.careCoverage,
                    mall: mallId,
                    // För att låta autocompleten ha kvar vald värde för mall
                    mallAutocomplete: mallId,
                },
            }),
        });
    };

    const resetFormValues = () => {
        formHelpers?.setFormValues({
            reset: true,
            values: getDefaultFormValues({ values: { mall: null } }),
        });
    };

    const showDialog = ({ type, callback }: { type: "change" | "clear"; callback: () => void }) => {
        emitter.emit("open-dialog", {
            type: "warning",
            description:
                type === "change"
                    ? "Vill du byta mall? Om du väljer att byta så kommer allt du fyllt i rensas."
                    : "Vill du rensa hela formuläret?",
            buttons: [
                {
                    title: "Nej",
                    type: "secondary",
                    action: () => {
                        skipNextOnValue.value = true;
                        formHelpers?.setFieldValue({
                            name: "mallAutocomplete",
                            value: formHelpers?.getFieldValue({
                                name: "mall",
                            }),
                        });
                    },
                },
                {
                    title: type === "change" ? "Ja, byt mall" : "Ja, rensa allt",
                    type: "primary",
                    action: async () => {
                        callback?.();
                    },
                },
            ],
        });
    };

    /**
     * När värde valts i autocompleten och satts på field "mallAutocomplete" i formData
     */
    const onValue = async ({ value }: { value: unknown }) => {
        if (skipNextOnValue.value) {
            skipNextOnValue.value = false;

            return;
        }

        if (!value) {
            return;
        }

        const typedValue = value as string;
        const oldValue = formHelpers?.getFieldValue({ name: "mall" });

        if (typedValue === oldValue) {
            return;
        }

        try {
            if (oldValue && oldValue !== typedValue) {
                showDialog({
                    type: "change",
                    callback: () => {
                        setFormValues({ mallId: typedValue });
                    },
                });

                return;
            }

            setFormValues({ mallId: typedValue });
        } catch (error) {
            console.error(error);
        }
    };

    const onClear = () => {
        if (formHelpers?.hasUnsavedData()) {
            showDialog({
                type: "clear",
                callback: () => {
                    resetFormValues();
                },
            });

            return;
        }

        resetFormValues();
    };

    onBeforeMount(() => {
        store.dispatch("receptforskrivningMall/addSubscriber");
    });

    onUnmounted(() => {
        store.dispatch("receptforskrivningMall/removeSubscriber");
    });
</script>

<template>
    <KGroup freeFlow name="mall" class="mall-autocomplete span-three" background="blue" rowGap="half">
        <!-- Separat fält för att tillåta lyssnare på byte av värde i autocomplete INNAN förändring i form data -->
        <KText hidden ignoreEmptyData name="mall" />
        <KAutocomplete
            ref="autocompleteRef"
            class="autocomplete"
            @inputValue="onValue"
            :options="onSearch"
            :onSearch="onSearch"
            :clearable="false"
            v-bind="$attrs"
            name="mallAutocomplete"
            label="Mall"
            placeholder="Sök bland mina egna och gemensamma mallar..."
        />
        <KButton type="button" size="small" width="content" align="end" variant="grey" @click="onClear">
            <img src="@/assets/cross_circle.svg" alt="Rensa allt" />
            <span>Rensa allt</span>
        </KButton>
    </KGroup>
</template>

<style lang="scss" scoped>
    .mall-autocomplete {
        .autocomplete {
            flex-grow: 1;
        }
    }
</style>
